<template>
  <div class="slip-body relative flex flex-col min-h-0" :class="{ 'h-full': scroll }">
    <div v-if="!legs.length" class="px-4 py-6 text-center text-xs text-gray-700">
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="1.5" class="mx-auto mb-2 text-brandGrey" aria-hidden="true">
        <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" /><path d="M9 8h6M9 12h6" />
      </svg>
      <p class="font-semibold">Add a prediction to start a slip</p>
      <p class="text-brandGrey mt-1">Tap the + next to any percentage on a match card, or build one below.</p>
    </div>

    <div v-else class="legs px-4" :class="{ 'flex-1 overflow-y-auto min-h-0': scroll }">
      <LegRow
        v-for="leg in legs"
        :key="leg.id"
        :leg="leg"
        :priced="pricedLeg(leg.id)"
        :locked="legLocked(leg.id)"
        :justAdded="justAdded === leg.id"
        @remove="remove"
        @swap="swap"
      />
    </div>

    <p v-if="error" class="px-4 pt-2 text-xs text-red-600" role="status">{{ error }}</p>
    <!-- what the build did, as a pop-up over the slip that goes again by itself -->
    <transition name="build-note">
      <div
        v-if="buildNotes.length"
        class="absolute left-4 right-4 top-3 z-20 rounded-xl bg-gray-100 border border-gray-200 shadow-lg px-3 py-2.5 text-xs text-gray-700 space-y-1"
        role="status"
        v-on:click="clearNotes"
      >
        <p v-for="note in buildNotes" :key="note">{{ note }}</p>
      </div>
    </transition>
    <p v-for="warning in warnings" :key="warning" class="px-4 pt-2 text-xxs text-brandGrey">
      {{ warning }}
    </p>

    <div class="h-3 flex-none"></div>
    <!-- the whole bottom is the match card's grey footer: totals, build, save / share, small print -->
    <div class="slip-footer mt-auto flex-none bg-gray-800/10 rounded-b-xl py-4 border-t border-gray-300">
    <div v-if="legs.length" class="px-4 pb-3">
      <SlipTotals
        :totals="priced && priced.totals"
        :stake="stake"
        :locked="locked"
        :pricing="pricing"
        @stake="setStake"
      />
    </div>

    <!-- a thin breaker between the numbers and the actions -->
    <div v-if="legs.length" class="mx-4 mb-3 border-t border-gray-300"></div>
    <div class="px-4 space-y-3 flex-none">
      <div v-if="legs.length && page" class="text-center">
        <button type="button" class="text-blue-700 underline uppercase text-xs" v-on:click="clear">Clear</button>
      </div>

      <form
        v-if="member"
        class="build flex flex-wrap items-center justify-center gap-2 text-xs"
        v-on:submit.prevent="build"
      >
        <label :for="buildId" class="text-gray-700">
          {{ legs.length ? "Add legs to reach odds of" : "Build me a slip at odds of" }}
        </label>
        <input
          :id="buildId"
          v-model="target"
          type="text"
          :inputmode="fractional ? 'text' : 'decimal'"
          autocomplete="off"
          autocapitalize="off"
          spellcheck="false"
          :placeholder="fractional ? '4/1' : '5.0'"
          class="w-16 border rounded bg-white px-2 py-1 tabular-nums text-center"
          :class="targetError ? 'border-red-500' : 'border-gray-300'"
          :aria-label="fractional ? 'Target odds, as a fraction like 4/1' : 'Target odds, decimal'"
          :aria-invalid="targetError ? 'true' : 'false'"
          :aria-describedby="targetError ? `${buildId}-error` : null"
          v-on:input="targetError = ''"
        />
        <button
          type="submit"
          class="btn-build rounded-lg bg-gray-300 text-gray-800 uppercase font-bold px-3 py-1"
          :disabled="pricing"
        >
          Build
        </button>
        <p v-if="targetError" :id="`${buildId}-error`" class="w-full text-center text-red-600" role="alert">
          {{ targetError }}
        </p>
      </form>
      <p v-else-if="!legs.length || locked" class="text-center text-xs">
        <router-link to="/signup/" class="text-blue-700 underline uppercase font-bold"
          >Upgrade to build priced slips →</router-link
        >
      </p>

      <!-- save and share together on the last row: plain blue icons, no background -->
      <div v-if="legs.length" class="flex items-center justify-center gap-6 text-blue-700">
        <button
          v-if="!locked"
          type="button"
          class="icon-btn"
          :disabled="saving"
          :title="savedId ? 'Saved' : 'Save slip'"
          :aria-label="savedId ? 'Saved' : 'Save slip'"
          v-on:click="saveSlip"
        >
          <!-- a bookmark: outlined to save, filled once saved -->
          <svg viewBox="0 0 24 24" width="22" height="22" :fill="savedId ? 'currentColor' : 'none'" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        </button>
        <div v-click-outside="closeShare" class="relative">
          <button
            type="button"
            class="icon-btn"
            :disabled="saving || imaging"
            title="Share"
            aria-label="Share"
            aria-haspopup="menu"
            :aria-expanded="String(shareOpen)"
            v-on:click="shareOpen = !shareOpen"
          >
            <!-- a box with an arrow coming out of it -->
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" />
            </svg>
          </button>
          <div
            v-if="shareOpen"
            role="menu"
            class="absolute bottom-full left-1/2 -translate-x-1/2 transform mb-2 z-30 w-40 rounded-xl bg-white border border-gray-200 shadow-lg py-1 text-sm text-gray-800"
          >
            <!-- a picture of the slip for socials: predictions only, open to everyone -->
            <button type="button" role="menuitem" class="share-item" v-on:click="pickShare('image')">Share image</button>
            <button v-if="!locked" type="button" role="menuitem" class="share-item" v-on:click="pickShare('link')">
              Share link
            </button>
          </div>
        </div>
      </div>
      <p v-if="actionStatus" class="text-xxs text-center text-gray-500" role="status">{{ actionStatus }}</p>
      <p v-if="shareUrl && !shared" class="text-xxs text-center text-gray-700 break-all">
        {{ shareUrl }}
      </p>
    </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import LegRow from "./LegRow.vue";
import SlipTotals from "./SlipTotals.vue";
import { parseOdds, oddsInput } from "../../odds";
import { trackFeature } from "../../analytics";
import ClickOutside from "vue-click-outside";
import { slipImage } from "./slipImage";

let uid = 0;

export default {
  name: "SlipBody",
  components: { LegRow, SlipTotals },
  directives: { ClickOutside },
  props: {
    page: { type: Boolean, default: false },
    scroll: { type: Boolean, default: false },
  },
  data() {
    uid += 1;
    return {
      buildId: `slip-build-${uid}`,
      target: oddsInput(5, this.$store.getters.preferences.odds_format),
      targetError: "",
      shareOpen: false,
      saving: false,
      savedId: null,
      shared: false,
      shareUrl: "",
      imaging: false,
      imageDone: false,
    };
  },
  computed: {
    ...mapState("betslip", ["legs", "stake", "priced", "pricing", "error", "justAdded", "buildNotes"]),
    ...mapGetters("betslip", ["locked", "legLocked", "member"]),
    warnings() {
      return (this.priced && this.priced.warnings) || [];
    },
    oddsFormat() {
      return this.$store.getters.preferences.odds_format;
    },
    // the target box takes odds the way the user reads them: 4/1 or 5.0
    fractional() {
      return this.oddsFormat !== "decimal";
    },
    // what the last tap did, in words under the icons
    actionStatus() {
      if (this.imaging) return "Making image…";
      if (this.imageDone) return "Image saved";
      if (this.saving) return "One moment…";
      if (this.shared) return "Link copied";
      if (this.savedId) return "Saved to My slips";
      return "";
    },
  },
  watch: {
    // keep the same price in the box when the user switches format
    oddsFormat(format, previous) {
      const decimal = parseOdds(this.target, previous);
      this.target = oddsInput(decimal || 5, format);
      this.targetError = "";
    },
    // a pop-up for a few seconds, a little longer when there is more to read
    buildNotes(notes) {
      clearTimeout(this.noteTimer);
      if (notes.length) this.noteTimer = setTimeout(this.clearNotes, 3500 + 1500 * notes.length);
    },
    legs() {
      this.savedId = null;
      this.shared = false;
      this.shareUrl = "";
    },
  },
  beforeDestroy() {
    clearTimeout(this.noteTimer);
  },
  methods: {
    clearNotes() {
      clearTimeout(this.noteTimer);
      this.$store.commit("betslip/setBuildNotes", []);
    },
    pricedLeg(id) {
      return this.$store.getters["betslip/pricedLeg"](id);
    },
    remove(id) {
      this.$store.dispatch("betslip/remove", id);
    },
    swap(payload) {
      trackFeature("betslip_swap", "betslip");
      this.$store.dispatch("betslip/swap", payload);
    },
    setStake(value) {
      this.$store.dispatch("betslip/setStake", value);
    },
    clear() {
      this.$store.dispatch("betslip/clear");
    },
    build() {
      const decimal = parseOdds(this.target, this.oddsFormat);
      if (!decimal) {
        this.targetError = this.fractional ? "Type odds like 4/1 or 11/4." : "Type odds like 5.0.";
        return;
      }
      if (decimal < 1.5 || decimal > 101) {
        this.targetError = this.fractional
          ? "Pick odds between 1/2 and 100/1."
          : "Pick odds between 1.5 and 100.";
        return;
      }
      this.targetError = "";
      this.$store.dispatch("betslip/build", { targetOdds: decimal });
    },
    // pasted into a bookmaker's search or a group chat: one leg per numbered line, then where
    // and when, then the price we saw
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (e) {
        const area = document.createElement("textarea");
        area.value = text;
        document.body.appendChild(area);
        area.select();
        let ok = false;
        try {
          ok = document.execCommand("copy");
        } catch (err) {
          ok = false;
        }
        document.body.removeChild(area);
        return ok;
      }
    },
    closeShare() {
      this.shareOpen = false;
    },
    pickShare(kind) {
      this.shareOpen = false;
      if (kind === "image") this.shareImage();
      else this.shareSlip();
    },
    async shareImage() {
      this.imaging = true;
      trackFeature("betslip_share_image", "betslip", { legs: this.legs.length, locked: this.locked });
      try {
        const legs = this.legs.map((leg) => {
          const priced = this.pricedLeg(leg.id) || {};
          return {
            label: leg.label || priced.label,
            match: leg.match || priced.match,
            kickoff: leg.kickoff || priced.kickoff,
            side: leg.side || priced.side,
            p: priced.locked || priced.p === undefined ? null : priced.p,
          };
        });
        const totals = (this.priced && this.priced.totals) || {};
        const blob = await slipImage({ legs, chance: this.locked ? null : totals.p });
        const file = new File([blob], "numbersgame-slip.png", { type: "image/png" });
        // phones: the share sheet (TikTok, Instagram, WhatsApp); elsewhere: download it
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], text: "My slip on numbersgame.uk" });
          } catch (e) {
            // the user closed the share sheet
          }
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 1000);
          this.imageDone = true;
          setTimeout(() => (this.imageDone = false), 2000);
        }
      } catch (e) {
        this.$store.commit("betslip/setError", "Couldn't make the image. Try again.");
      }
      this.imaging = false;
    },
    async saveSlip() {
      this.saving = true;
      try {
        const saved = await this.$store.dispatch("betslip/save");
        this.savedId = saved && saved.slip_id;
      } catch (e) {
        this.$store.commit("betslip/setError", "Couldn't save the slip. Try again.");
      }
      this.saving = false;
    },
    async shareSlip() {
      this.saving = true;
      try {
        const url = await this.$store.dispatch("betslip/share");
        if (url) {
          this.shareUrl = url;
          this.shared = await this.copyText(url);
          setTimeout(() => (this.shared = false), 2000);
        }
      } catch (e) {
        this.$store.commit("betslip/setError", "Couldn't make a link. Try again.");
      }
      this.saving = false;
    },
  },
};
</script>

<style scoped>
/* the match card footer's inset shadow */
.slip-footer {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.1);
}
.icon-btn {
  width: 40px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
/* the design language's grey button: line fill, radius-lg, lifts on hover */
.btn-build {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.btn-build:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15), 0 4px 15px rgba(0, 0, 0, 0.1);
}
.icon-btn:disabled {
  opacity: 0.5;
}
.share-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.875rem;
}
.share-item:hover {
  background: #f3f4f6;
}
.build-note-enter-active,
.build-note-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.build-note-enter,
.build-note-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
